import OrdersReportsPage from './OrdersReportsPage';
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ResponsiveContainer, Tooltip, ScatterChart, Scatter, XAxis, YAxis, ZAxis, LineChart, CartesianGrid, ReferenceArea, Legend, Line, ReferenceLine } from "recharts";
import { useLayoutImage } from "../context/LayoutContext";
import config from "../config";
import "./Reports.css";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import i18n from "../i18n";

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

const tr = i18n.t.bind(i18n);

const Reports = () => {

  const backgroundImage = useLayoutImage();
  const [reservationData, setReservationData] = useState([]);
  const [hiddenLines, setHiddenLines] = useState({ walked_in: false, reservations: false, total: false });
  const [hiddenMeatLines, setHiddenMeatLines] = useState({});
  const [meatData, setMeatData] = useState([]);
  const [meatTypes, setMeatTypes] = useState([]);
  const [kitchenData, setKitchenData] = useState([]);
  const [hiddenKitchenLines, setHiddenKitchenLines] = useState({});
  const [stateTypes, setStateTypes] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [hiddenServiceLines, setHiddenServiceLines] = useState({});
  const [serviceTypes, setServiceTypes] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [heatmapRawPoints, setHeatmapRawPoints] = useState([]);
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });
  const [consumptionPerResData, setConsumptionPerResData] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [hiddenFeedbackLines, setHiddenFeedbackLines] = useState({
    avg_org_service: false,
    avg_quality_cost: false,
    avg_environment_clean: false,
  });

  const imageRef = useRef(null);

  // Η περίοδος επιλέγεται από το μενού πλοήγησης: /reports, ?g=week, ?g=month
  const location = useLocation();
  const gParam = new URLSearchParams(location.search).get("g");
  const granularity = gParam === "week" ? "week" : gParam === "month" ? "month" : "day";
  
  const today = dayjs().format("YYYY-MM-DD");
  
  const todayX =
    granularity === "week"
      ? dayjs().startOf("isoWeek").format("YYYY-MM-DD")
      : today;
  
  // Παράθυρα παρατήρησης — ΠΡΕΠΕΙ να ταιριάζουν με τα WINDOWS στο api/routes/reports.js
  const dayStart = dayjs().subtract(35, "day");
  const dayEnd = dayjs().add(7, "day");

  const weekStart = dayjs().subtract(13, "week").startOf("isoWeek"); // Δευτέρα
  const weekEnd = dayjs().add(1, "week").startOf("isoWeek");

  const monthStart = dayjs().subtract(12, "month").startOf("month");
  const monthEnd = dayjs().add(1, "month").startOf("month");

  // Ένα σημείο αλήθειας για το παράθυρο κάθε ανάλυσης
  const windowFor = (g) =>
    g === "week" ? { start: weekStart, end: weekEnd }
    : g === "month" ? { start: monthStart, end: monthEnd }
    : { start: dayStart, end: dayEnd };
  
  const sleep = (ms) => new Promise(res => setTimeout(res, ms));

  useEffect(() => {
    let cancelled = false;
  
    (async () => {
      await fetchReservations();
      if (cancelled) return;
  
      await sleep(300);
      if (cancelled) return;
  
      await fetchMeatConsumption();
      if (cancelled) return;

      await sleep(300);
      if (cancelled) return;
      
      await fetchFeedbackReport();
      if (cancelled) return;
  
      await sleep(300);
      if (cancelled) return;
  
      await fetchKitchenResponsiveness();
      if (cancelled) return;
  
      await sleep(300);
      if (cancelled) return;
  
      await fetchServiceResponsiveness();
    })();
  
    return () => { cancelled = true; };
  }, [granularity]);
  
  useEffect(() => {
    // delay heatmap so charts appear immediately
    const t = setTimeout(() => {
      fetchHeatmapData();
    }, 1200); 
    
    return () => clearTimeout(t);
  }, [granularity]);
  
  useEffect(() => {
    // Get the image's real dimensions
    const updateImageSize = () => {
      if (imageRef.current) {
        setImageSize({
          width: imageRef.current.clientWidth,
          height: imageRef.current.clientHeight,
        });
      }
    };

    window.addEventListener("resize", updateImageSize);
    updateImageSize();

    return () => window.removeEventListener("resize", updateImageSize);
  }, []);

  useEffect(() => {
    if (!meatData?.length || !reservationData?.length) {
      setConsumptionPerResData([]);
      return;
    }
  
    // map reservations by date
    const resByDate = new Map(reservationData.map(r => [r.date, r]));
  
    const getTotalConsumption = (row) => {
      // backend sets "Total" (capital T) in /reports/consumption :contentReference[oaicite:3]{index=3}
      if (row && row.Total != null) return Number(row.Total) || 0;
  
      // fallback: sum all numeric keys except date (just in case)
      let sum = 0;
      Object.keys(row || {}).forEach(k => {
        if (k === "date") return;
        const v = Number(row[k] || 0);
        if (Number.isFinite(v)) sum += v;
      });
      return sum;
    };
  
    const ratio = meatData.map(m => {
      const r = resByDate.get(m.date);
      const totalCons = getTotalConsumption(m); // kg
      const totalPeople = Number(r?.people || 0); // <-- from /reports/reservations
    
      return {
        date: m.date,
        g_per_person: totalPeople > 0 ? ((totalCons * 1000) / totalPeople) : 0
      };
    });
  
    setConsumptionPerResData(ratio);
  }, [meatData, reservationData]);


  const fetchReservations = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/reservations', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
  
      if (response.data.length > 0) {
        // Extract all series keys dynamically (now also includes 'cancellations')
        const reservationKeys = Object.keys(response.data[0]).filter(
          key => key !== "date" && key !== "people"
        );
        
        const initHidden = {};
        reservationKeys.forEach(k => { initHidden[k] = false; });
        setHiddenLines(initHidden);
  
        const { start, end } = windowFor(granularity);
        const formattedData = fillMissingDates(response.data, reservationKeys, start, end, granularity);
  
        setReservationData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching reservation data:", error);
    }
  };

  const fetchMeatConsumption = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/consumption', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
      
      if (response.data.length > 0) {

         const meat_types = Object.keys(response.data[0]).filter(key => key !== "date");
         setMeatTypes(meat_types);
    
         // legend visibility map
         const hidden_meat_lines = { total_meat: false };
         meat_types.forEach(mt => (hidden_meat_lines[mt] = false));
         setHiddenMeatLines(hidden_meat_lines);
    
         // pad to the same window used by Reservations Overview
          const { start, end } = windowFor(granularity);
          const numeric = response.data.map(r => {
            const obj = { ...r };
            meat_types.forEach(k => { obj[k] = Number(obj[k] || 0); });
            return obj;
          });
          const formatted = fillMissingDates(numeric, meat_types, start, end, granularity);
          setMeatData(formatted);

      }
    } catch (error) {
      console.error("Error fetching meat consumption data:", error);
    }
  };

  const fetchKitchenResponsiveness = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/kitchen', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
      
      if (response.data.length > 0) {
        // Get all state types from the first entry
        let state_types = new Set();
        response.data.forEach(entry => {
          Object.keys(entry).forEach(key => {
            if (key !== "date") state_types.add(key);
          });
        });
        
        state_types = [...state_types]; // Convert Set to Array
        setStateTypes(state_types);
  
        // Initialize hidden lines for each state
        let hidden_state_lines = {};
        state_types.forEach(state => {
          hidden_state_lines[state] = false;
        });
        setHiddenKitchenLines(hidden_state_lines);
  
        // Convert MM:SS format to total seconds
        const formattedData = response.data.map(entry => {
          let newEntry = { date: entry.date };
          state_types.forEach(state => {
            if (entry[state]) {
              const timeParts = entry[state].split(":").map(Number);
              const totalSeconds = timeParts[0] * 60 + timeParts[1]; // Convert MM:SS to total seconds
              newEntry[state] = totalSeconds;
            } else {
              newEntry[state] = 0; // Ensure missing states have a value
            }
          });
          return newEntry;
        });
  
        const { start, end } = windowFor(granularity);
        setKitchenData(fillMissingDates(formattedData, state_types, start, end, granularity));
      }
    } catch (error) {
      console.error("Error fetching kitchen responsiveness data:", error);
    }
  };

  const fetchServiceResponsiveness = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/service', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
      
      if (response.data.length > 0) {
        // Get all service action types
        let service_types = new Set();
        response.data.forEach(entry => {
          Object.keys(entry).forEach(key => {
            if (key !== "date") service_types.add(key);
          });
        });
        
        service_types = [...service_types]; // Convert Set to Array
        setServiceTypes(service_types);
  
        // Initialize hidden lines for each service action
        let hidden_service_lines = {};
        service_types.forEach(action => {
          hidden_service_lines[action] = false;
        });
        setHiddenServiceLines(hidden_service_lines);
  
        // Convert MM:SS format to total seconds
        const formattedData = response.data.map(entry => {
          let newEntry = { date: entry.date };
          service_types.forEach(action => {
            if (entry[action]) {
              const timeParts = entry[action].split(":").map(Number);
              const totalSeconds = timeParts[0] * 60 + timeParts[1]; // Convert MM:SS to total seconds
              newEntry[action] = totalSeconds;
            } else {
              newEntry[action] = 0; // Ensure missing states have a value
            }
          });
          return newEntry;
        });
  
        const { start, end } = windowFor(granularity);
        setServiceData(fillMissingDates(formattedData, service_types, start, end, granularity));
      }
    } catch (error) {
      console.error("Error fetching service responsiveness data:", error);
    }
  };
  
  const fetchFeedbackReport = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/feedback', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
  
      const keys = ["avg_org_service", "avg_quality_cost", "avg_environment_clean"];
  
      const { start, end } = windowFor(granularity);
  
      const normalized = (response.data || []).map(r => ({
        date: r.date,
        avg_org_service: Number(r.avg_org_service || 0),
        avg_quality_cost: Number(r.avg_quality_cost || 0),
        avg_environment_clean: Number(r.avg_environment_clean || 0),
        comments: Array.isArray(r.comments) ? r.comments : [],
      }));
  
      // Fill missing buckets; keep comments=[]
      const filled = fillMissingDates(normalized, keys, start, end, granularity).map(row => ({
        ...row,
        comments: Array.isArray(row.comments) ? row.comments : [],
      }));
  
      setFeedbackData(filled);
    } catch (e) {
      console.error("Error fetching feedback report:", e);
      setFeedbackData([]);
    }
  };

  const fetchHeatmapData = async () => {
    try {
      const response = await axios.get(
        tr('{{API_BASE_URL}}/reports/heatmap', { API_BASE_URL: config.API_BASE_URL }),
        { params: { group: granularity }, withCredentials: true }
      );
  
      // 1) raw table points (unmodified)
      const raw = (response.data || []).map(p => ({
        ...p,
        x: Number(p.x),
        y: Number(p.y),
      }));
  
      // 2) Apply calibration to table points (THIS is the key change)
      const calibrated = applyTableCalibration(raw);
  
      // keep for debug if needed
      setHeatmapRawPoints(calibrated);
  
      // 3) Run your existing interpolation on CALIBRATED table positions
      const run = () => {
        const grid = generateInterpolatedHeatmap(calibrated); // heavy
        setHeatmapData(grid);
      };
      
      // Prefer idle time, fallback to setTimeout
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(run, { timeout: 1000 });
      } else {
        setTimeout(run, 0);
      }
  
    } catch (error) {
      console.error("Error fetching heatmap data:", error);
    }
  };
  
  const formatSmartNumber = (n, decimals = 2) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "0";
  
    // if it's basically an integer, show as int
    if (Math.abs(v - Math.round(v)) < 1e-9) return String(Math.round(v));
  
    // otherwise show decimals (trim trailing zeros)
    return v.toFixed(decimals).replace(/\.?0+$/, "");
  };
  
  // for seconds -> minutes tooltips (avoid 0 for < 1 minute)
  const formatMinutesSmart = (seconds) => {
    const v = Number(seconds);
    if (!Number.isFinite(v)) return "0";
    const mins = v / 60;
    return formatSmartNumber(mins, 2);
  };
  
  const makeSortedPositiveTooltip = ({ labelFormatter, valueFormatter, isHidden, nameMap }) => {
    return function SortedPositiveTooltip({ active, payload, label }) {
      if (!active || !payload || !payload.length) return null;
  
      const rows = payload
        .filter(p => p && p.dataKey != null)
        .filter(p => {
          const key = String(p.dataKey);
          if (isHidden && isHidden(key)) return false;
  
          const v = Number(p.value || 0);
          return Number.isFinite(v) && v > 0; // only positive
        })
        .sort((a, b) => Number(b.value || 0) - Number(a.value || 0)); // desc
  
      if (!rows.length) return null;
  
      return (
        <div style={{
          background: "rgba(255,255,255,0.96)",
          border: "1px solid rgba(0,0,0,0.15)",
          borderRadius: 10,
          padding: "8px 10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)"
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {labelFormatter ? labelFormatter(label) : label}
          </div>
  
          {rows.map((p) => {
            const key = String(p.dataKey);
            const displayName = (nameMap && nameMap[key]) || p.name || key;
            const formatted = valueFormatter ? valueFormatter(p.value, key) : p.value;
  
            return (
              <div key={key} style={{ display: "flex", gap: 10, alignItems: "center", margin: "2px 0" }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 999,
                  background: p.color || "#888", border: "1px solid rgba(0,0,0,0.15)"
                }} />
                <span style={{ flex: 1 }}>{displayName}</span>
                <span style={{ fontWeight: 600 }}>{formatted}</span>
              </div>
            );
          })}
        </div>
      );
    };
  };

  const FeedbackTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
  
    const row = payload?.[0]?.payload || {};
    const comments = Array.isArray(row.comments) ? row.comments : [];
  
    const val = (k) => {
      const p = payload.find(x => x.dataKey === k);
      const v = Number(p?.value || 0);
      return Number.isFinite(v) ? v.toFixed(2) : "0.00";
    };
  
    const labelText = tooltipLabel(label);
  
    return (
      <div style={{
        background: "rgba(255,255,255,0.96)",
        border: "1px solid rgba(0,0,0,0.15)",
        borderRadius: 10,
        padding: "8px 10px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        maxWidth: 420
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{labelText}</div>
  
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <div>{tr("Managment & Service")}: <b>{val("avg_org_service")}</b></div>
          <div>{tr("Quality / Cost")}: <b>{val("avg_quality_cost")}</b></div>
          <div>{tr("Environment & Cleanliness")}: <b>{val("avg_environment_clean")}</b></div>
        </div>
  
        {comments.length > 0 && (
          <div style={{ marginTop: 6, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{tr("Comments")}</div>
            <div style={{ maxHeight: 140, overflow: "auto", fontSize: 12 }}>
              {comments.slice(0, 12).map((c, idx) => (
                <div key={idx} style={{ marginBottom: 6 }}>
                  <div style={{ opacity: 0.85 }}>
                    <b>{c.client_name || tr("Unknown")}</b> — #{c.reservation_id}
                  </div>
                  <div style={{ fontStyle: "italic" }}>{c.comment}</div>
                </div>
              ))}
              {comments.length > 12 && (
                <div style={{ opacity: 0.75 }}>{tr("…and more")}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };


  const bucketKey = (dateStr, group) => {
    const d = dayjs(dateStr);
    if (group === "week") return d.startOf("isoWeek").format("YYYY-MM-DD"); // ΠΑΝΤΑ Δευτέρα
    if (group === "month") return d.startOf("month").format("YYYY-MM-DD");  // ΠΑΝΤΑ 1η
    return d.format("YYYY-MM-DD");
  };
  
  // Replace fillMissingDates with this version
  const fillMissingDates = (data, keys, startDate, endDate, group) => {
    const unit = group === "week" ? "week" : group === "month" ? "month" : "day";
  
    const filled = [];
  
    // normalize incoming data keys
    const dataMap = new Map();
    (data || []).forEach(item => {
      const k = bucketKey(item.date, group);
      // merge if duplicates happen after normalization
      if (!dataMap.has(k)) dataMap.set(k, { ...item, date: k });
      else {
        const prev = dataMap.get(k);
        const merged = { ...prev };
        (keys || []).forEach(seriesKey => {
          merged[seriesKey] = Number(prev[seriesKey] || 0) + Number(item[seriesKey] || 0);
        });
        dataMap.set(k, merged);
      }
    });
  
    let cursor = startDate.startOf(unit);
    const end = endDate.startOf(unit);
  
    while (cursor.isBefore(end) || cursor.isSame(end)) {
      const dateStr =
        group === "week" ? cursor.startOf("isoWeek").format("YYYY-MM-DD")
        : group === "month" ? cursor.startOf("month").format("YYYY-MM-DD")
        : cursor.format("YYYY-MM-DD");
  
      if (dataMap.has(dateStr)) {
        filled.push(dataMap.get(dateStr));
      } else {
        const empty = { date: dateStr };
        (keys || []).forEach(k => (empty[k] = 0));
        filled.push(empty);
      }
  
      cursor = cursor.add(1, unit);
    }
  
    return filled;
  };

  const xTick = (d) => {
    const dt = dayjs(d);
    if (granularity === "week") {
      const w = String(dt.isoWeek()).padStart(2, "0");
      return `W${w} (${dt.format("DD/MM")})`;
    }
    if (granularity === "month") return dt.format("MMM YY");
    return dt.format("DD/MM");
  };
  
  const tooltipLabel = (d) => {
    const dt = dayjs(d);
    if (granularity === "week") {
      const w = String(dt.isoWeek()).padStart(2, "0");
      return `W${w} (${dt.format("DD/MM")})`;
    }
    return tr('{{var1}}', { var1: dt.format("dddd, DD/MM") });
  };

  // This must match the coordinate system of your tables in DB/API.
  // If your table editor uses an 800x800 canvas, keep these.
  const HEATMAP_BASE_W = 800;
  const HEATMAP_BASE_H = 800;
  
  const TABLE_CAL = {
    ax: 0.99,
    bx: 25.0,
    ay: 1.02,
    by: -25.0,
  };
  
  const applyTableCalibration = (pts) => {
    return (pts || []).map(p => ({
      ...p,
      x: TABLE_CAL.ax * Number(p.x) + TABLE_CAL.bx,
      y: TABLE_CAL.ay * (HEATMAP_BASE_H - Number(p.y)) + TABLE_CAL.by,
    }));
  };

  const generateInterpolatedHeatmap = (data, gridSize = 10, sigma = .6) => {
    if (data.length === 0) return [];
  
    // Use the same coordinate system as your stored table positions
    const minX = 0;
    const maxX = HEATMAP_BASE_W;
    const minY = 0;
    const maxY = HEATMAP_BASE_H;
    
    let interpolatedData = [];
  
    for (let x = minX; x <= maxX; x += gridSize) {
      for (let y = minY; y <= maxY; y += gridSize) {
        let weightedSum = 0;
        let weightTotal = 0;
  
        data.forEach(({ x: px, y: py, reservation_count }) => {
          let distance = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
  
          // Apply Gaussian weight function
          let weight = Math.exp(-((distance ** 2) / (2 * sigma ** 2)));
  
          weightedSum += reservation_count * weight;
          weightTotal += weight;
        });
  
        let interpolatedValue = weightTotal > 0 ? weightedSum / weightTotal : 0;
  
        // Normalize interpolated value to prevent extreme values
        interpolatedValue = Math.max(0, interpolatedValue);
  
        interpolatedData.push({ x, y, reservation_count: interpolatedValue });
      }
    }
  
    return interpolatedData;
  };
  
  // ---------- Helpers for "rescale based on visible lines" ----------
  const visibleKeys = (hiddenMap, allKeys) => (allKeys || []).filter(k => !hiddenMap?.[k]);

  const dataMaxForKeys = (data, keys) => {
    if (!data || !data.length || !keys || !keys.length) return 0;
    let max = 0;
    for (const row of data) {
      for (const k of keys) {
        const v = Number(row?.[k] ?? 0);
        if (Number.isFinite(v) && v > max) max = v;
      }
    }
    return max;
  };

  // Stable color per key (instead of Math.random on every render)
  const colorForKey = (key) => {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    return `hsl(${hue}, 70%, 40%)`;
  };

  const LegendBlock = ({ keys, hiddenMap, onToggle, colorMap, labelMap }) => (
    <div className="chart-legend">
      <div className="legend-items">
        {keys.map((k) => {
          const isHidden = !!hiddenMap?.[k];
          const color = colorMap?.[k] || colorForKey(k);

          return (
            <div
              key={k}
              className={`legend-item ${isHidden ? "legend-hidden" : ""}`}
              onClick={() => onToggle({ dataKey: k })}
              title={isHidden ? tr("Show") : tr("Hide")}
            >
              <span className="legend-dot" style={{ background: color }} />
              <span>{(labelMap && labelMap[k]) || k}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
  

  // Handle Legend Click to Toggle Lines
  const handleLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  const handleMeatLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenMeatLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  const handleKitchenLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenKitchenLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  const handleServiceLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenServiceLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  const handleFeedbackLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenFeedbackLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  // Toggle all series in a section on/off.
  // 'hidden' is the visibility map (false=shown, true=hidden)
  const toggleAll = (hidden, setHidden) => {
    if (!hidden) return;
    const allShown = Object.values(hidden).every(v => v === false);
    const next = {};
    Object.keys(hidden).forEach(k => { next[k] = allShown ? true : false; });
    setHidden(next);
  };

  // Get highlight ranges
  const highlightStart = reservationData.length ? reservationData[0].date : null;
  const highlightEnd = reservationData.length ? reservationData[7].date : null;
  const lastWeekStart = reservationData.length ? reservationData[reservationData.length - 8].date : null;
  const lastWeekEnd = reservationData.length ? reservationData[reservationData.length - 1].date : null;

  // Find all Monday dates
  const mondayDates =
    granularity === "day"
      ? reservationData.filter(d => dayjs(d.date).day() === 1).map(d => d.date)
      : [];

  const scaleX = imageSize.width / HEATMAP_BASE_W;
  const scaleY = imageSize.height / HEATMAP_BASE_H;
  
  const scaledHeatmapData = heatmapData.map(p => ({
    ...p,
    x: p.x * scaleX,
    y: p.y * scaleY,
  }));

  return (
    <div className="reports-container reportsPage">
      {/* Η επιλογή περιόδου έγινε υπομενού του «Reports» στην πλοήγηση */}
      <div className="reports-periodbar">
        <span className="reports-period">
          {granularity === "day" ? tr("Daily")
            : granularity === "week" ? tr("Weekly")
            : tr("Monthly")}
        </span>
        <span className="gran-note">
          {granularity === "day" ? tr("Last 5 weeks")
            : granularity === "week" ? tr("Last 3 months")
            : tr("Last 12 months")}
        </span>
      </div>

      <h2 style={{display:'flex', alignItems:'center', gap:12}}>{tr('Reservations Overview')}<label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:13, fontWeight:500}}>
         <input
           type="checkbox"
           checked={Object.values(hiddenLines).every(v => v === false)}
           onChange={() => toggleAll(hiddenLines, setHiddenLines)}
         />{tr('Select / unselect all')}</label>
      </h2>
      {reservationData && reservationData.length>0 && (() => {
        const reservationKeys = Object.keys(hiddenLines || {}).filter(k => k !== "date");
        const vKeys = visibleKeys(hiddenLines, reservationKeys);
        const maxY = dataMaxForKeys(reservationData, vKeys);
        const yTop = Math.max(5, Math.ceil(maxY * 1.15));

        // fixed colors like you already use
        const colors = {
          reservations: "#0088FE",
          walked_in: "#ff7300",
          total: "#00C49F",
          cancellations: "red",
        };

        return (
          <div className="chart-block">
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={reservationData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />

                {granularity === "day" && highlightStart && highlightEnd && (
                  <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
                {granularity === "day" && lastWeekStart && lastWeekEnd && (
                  <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}

                {mondayDates.map((date) => (
                  <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />
                ))}
                <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />

                <XAxis dataKey="date" tickFormatter={xTick} />
                <YAxis
                  domain={[0, yTop]}
                  tickFormatter={(value) => Math.round(value)}
                  allowDecimals={false}
                />

                <Tooltip
                  content={makeSortedPositiveTooltip({
                    labelFormatter: tooltipLabel,
                    valueFormatter: (v) => formatSmartNumber(v, 2),
                    isHidden: (k) => !!hiddenLines?.[k],
                    nameMap: {
                      reservations: tr("Reservations"),
                      walked_in: tr("Walked In"),
                      total: tr("Total"),
                      cancellations: tr("Cancellations"),
                    }
                  })}
                />

                {/* IMPORTANT: render lines only if visible */}
                {!hiddenLines.reservations && <Line type="monotone" dataKey="reservations" stroke={colors.reservations} name="Reservations" />}
                {!hiddenLines.walked_in && <Line type="monotone" dataKey="walked_in" stroke={colors.walked_in} name="Walked In" />}
                {!hiddenLines.total && <Line type="monotone" dataKey="total" stroke={colors.total} strokeWidth={2} name="Total" />}
                {!hiddenLines.cancellations && <Line type="monotone" dataKey="cancellations" stroke={colors.cancellations} name="Cancellations" />}
              </LineChart>
            </ResponsiveContainer>

            <LegendBlock
              keys={reservationKeys}
              hiddenMap={hiddenLines}
              onToggle={handleLegendClick}
              colorMap={colors}
            />
          </div>
        );
      })()}

      <br/><br/>
      <h2 style={{display:'flex', alignItems:'center', gap:12}}>{tr('Consumption Overview')}<label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:13, fontWeight:500}}>
          <input
            type="checkbox"
            checked={Object.values(hiddenMeatLines).every(v => v === false)}
            onChange={() => toggleAll(hiddenMeatLines, setHiddenMeatLines)}
          />{tr('Select / unselect all')}</label>
      </h2>
      {meatData && meatData.length>0 && (() => {
        const keys = meatTypes || [];
        const vKeys = visibleKeys(hiddenMeatLines, keys);
        const maxY = dataMaxForKeys(meatData, vKeys);
        const yTop = Math.max(1, Math.ceil(maxY * 1.15));

        return (
          <div className="chart-block">
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={meatData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />

                {granularity === "day" && highlightStart && highlightEnd && (
                  <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
                {granularity === "day" && lastWeekStart && lastWeekEnd && (
                  <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
                {mondayDates.map(date => <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />)}
                <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />

                <XAxis dataKey="date" tickFormatter={xTick} />
                <YAxis
                  domain={[0, yTop]}
                  tickFormatter={value => Math.round(value)}
                  allowDecimals={false}
                  label={{ value: "Quantity", angle: -90, position: "insideLeft" }}
                />
                <Tooltip
                  content={makeSortedPositiveTooltip({
                    labelFormatter: tooltipLabel,
                    valueFormatter: (v) => formatSmartNumber(v, 2),
                    isHidden: (k) => !!hiddenMeatLines?.[k],
                  })}
                />

                {/* Render only visible lines + stable colors */}
                {keys.map((meat, i) => (
                  hiddenMeatLines?.[meat] ? null : (
                    <Line
                      key={meat}
                      type="monotone"
                      dataKey={meat}
                      stroke={colorForKey(meat)}
                      name={meat}
                      strokeWidth={i < keys.length - 1 ? 1 : 2}
                      dot={false}
                    />
                  )
                ))}
              </LineChart>
            </ResponsiveContainer>

            <LegendBlock
              keys={keys}
              hiddenMap={hiddenMeatLines}
              onToggle={handleMeatLegendClick}
            />
          </div>
        );
      })()}
      
    <br/><br/>
    <h2>{tr('Consumption per Person')}</h2>
    
    {consumptionPerResData && consumptionPerResData.length > 0 && (() => {
      const maxY = dataMaxForKeys(consumptionPerResData, ["g_per_person"]);
      const yTop = Math.max(0.1, Math.ceil(maxY * 1.25 * 100) / 100);
    
      return (
        <div className="chart-block">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={consumptionPerResData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
    
              {granularity === "day" && highlightStart && highlightEnd && (
                <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
              )}
              {granularity === "day" && lastWeekStart && lastWeekEnd && (
                <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
              )}
    
              {mondayDates.map(date => (
                <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />
              ))}
              <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />
    
              <XAxis dataKey="date" tickFormatter={xTick} />
              <YAxis
                domain={[0, yTop]}
                tickFormatter={(v) => formatSmartNumber(v, 2)}
                allowDecimals={true}
                label={{ value: tr("g / person"), angle: -90, position: "insideLeft" }}
              />
    
              <Tooltip
                content={makeSortedPositiveTooltip({
                  labelFormatter: tooltipLabel,
                  valueFormatter: (v) => `${formatSmartNumber(v, 0)} ${tr("g / person")}`,
                  isHidden: () => false,
                  nameMap: { g_per_person: tr("Consumption per person") }
                })}
              />
    
              <Line
                type="monotone"
                dataKey="g_per_person"
                stroke="#6f42c1"
                strokeWidth={2}
                dot={false}
                name={tr("Consumption per person")}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    })()}
      
    <br/><br/>
    <h2 style={{display:'flex', alignItems:'center', gap:12}}>
      {tr("Clients Feedback")}
      <label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:13, fontWeight:500}}>
        <input
          type="checkbox"
          checked={Object.values(hiddenFeedbackLines).every(v => v === false)}
          onChange={() => toggleAll(hiddenFeedbackLines, setHiddenFeedbackLines)}
        />
        {tr('Select / unselect all')}
      </label>
    </h2>
    
    {feedbackData && feedbackData.length > 0 && (() => {
      const keys = ["avg_org_service", "avg_quality_cost", "avg_environment_clean"];
      const vKeys = visibleKeys(hiddenFeedbackLines, keys);
    
      // keep y fixed 0..5 (as you asked), so no rescale needed
      const colors = {
        avg_org_service: colorForKey("avg_org_service"),
        avg_quality_cost: colorForKey("avg_quality_cost"),
        avg_environment_clean: colorForKey("avg_environment_clean"),
      };
    
      return (
        <div className="chart-block">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={feedbackData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
    
              {granularity === "day" && highlightStart && highlightEnd && (
                <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
              )}
              {granularity === "day" && lastWeekStart && lastWeekEnd && (
                <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
              )}
    
              {mondayDates.map(date => (
                <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />
              ))}
              <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />
    
              <XAxis dataKey="date" tickFormatter={xTick} />
              <YAxis domain={[0, 5]} tickCount={6} allowDecimals />
    
              <Tooltip content={<FeedbackTooltip />} />
    
              {!hiddenFeedbackLines.avg_org_service && (
                <Line
                  type="monotone"
                  dataKey="avg_org_service"
                  stroke={colors.avg_org_service}
                  dot={false}
                  strokeWidth={2}
                  name={tr("Managment & Service")}
                />
              )}
    
              {!hiddenFeedbackLines.avg_quality_cost && (
                <Line
                  type="monotone"
                  dataKey="avg_quality_cost"
                  stroke={colors.avg_quality_cost}
                  dot={false}
                  strokeWidth={2}
                  name={tr("Quality / Cost")}
                />
              )}
    
              {!hiddenFeedbackLines.avg_environment_clean && (
                <Line
                  type="monotone"
                  dataKey="avg_environment_clean"
                  stroke={colors.avg_environment_clean}
                  dot={false}
                  strokeWidth={2}
                  name={tr("Environment & Cleanliness")}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
    
          <LegendBlock
            keys={keys}
            hiddenMap={hiddenFeedbackLines}
            onToggle={handleFeedbackLegendClick}
            colorMap={colors}
            labelMap={{
              avg_org_service: tr("Managment & Service"),
              avg_quality_cost: tr("Quality / Cost"),
              avg_environment_clean: tr("Environment & Cleanliness"),
            }}
          />
        </div>
      );
    })()}

    
      <br/><br/>
      <h2 style={{display:'flex', alignItems:'center', gap:12}}>{tr('Kitchen Responsiveness')}<label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:13, fontWeight:500}}>
          <input
            type="checkbox"
            checked={Object.values(hiddenKitchenLines).every(v => v === false)}
            onChange={() => toggleAll(hiddenKitchenLines, setHiddenKitchenLines)}
          />{tr('Select / unselect all')}</label>
      </h2>
      {(kitchenData && kitchenData.length > 0) && (() => {
        const keys = stateTypes || [];
        const vKeys = visibleKeys(hiddenKitchenLines, keys);
      
        const maxSec = dataMaxForKeys(kitchenData, vKeys);
        const yTop = Math.max(60, Math.ceil(maxSec * 1.15)); // at least 1 minute
      
        return (
          <div className="chart-block">
      
            <ResponsiveContainer width="100%" height={420}>
              <LineChart
                data={kitchenData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
      
                {granularity === "day" && highlightStart && highlightEnd && (
                  <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
                {granularity === "day" && lastWeekStart && lastWeekEnd && (
                  <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
      
                {mondayDates.map((date) => (
                  <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />
                ))}
                <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />
      
                <XAxis
                  dataKey="date"
                  tickFormatter={xTick}
                />
      
                <YAxis
                  domain={[0, yTop]}
                  tickFormatter={(value) => Math.round(value / 60)} // seconds -> minutes
                  allowDecimals={false}
                  label={{ value: tr("Minutes"), angle: -90, position: "insideLeft" }}
                />
      
                <Tooltip
                  content={makeSortedPositiveTooltip({
                    labelFormatter: tooltipLabel,
                    valueFormatter: (v) => `${formatMinutesSmart(v)} ${tr("Minutes")}`,
                    isHidden: (k) => !!hiddenKitchenLines?.[k],
                  })}
                />
      
                {/* Render ONLY visible lines so Y-axis rescales */}
                {keys.map((state, i) =>
                  hiddenKitchenLines?.[state] ? null : (
                    <Line
                      key={state}
                      type="monotone"
                      dataKey={state}
                      stroke={colorForKey(state)}
                      name={state}
                      strokeWidth={i === keys.length - 1 ? 2 : 1}
                      dot={false}
                    />
                  )
                )}
              </LineChart>
            </ResponsiveContainer>
      
            <LegendBlock
              keys={keys}
              hiddenMap={hiddenKitchenLines}
              onToggle={handleKitchenLegendClick}
            />
          </div>
        );
      })()}
      <br/><br/>
      <h2 style={{display:'flex', alignItems:'center', gap:12}}>{tr('Service Responsiveness')}<label style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:13, fontWeight:500}}>
          <input
            type="checkbox"
            checked={Object.values(hiddenServiceLines).every(v => v === false)}
            onChange={() => toggleAll(hiddenServiceLines, setHiddenServiceLines)}
          />{tr('Select / unselect all')}</label>
      </h2>
      {(serviceData && serviceData.length > 0) && (() => {
        const keys = serviceTypes || [];
        const vKeys = visibleKeys(hiddenServiceLines, keys);
      
        const maxSec = dataMaxForKeys(serviceData, vKeys);
        const yTop = Math.max(60, Math.ceil(maxSec * 1.15));
      
        return (
          <div className="chart-block">
      
            <ResponsiveContainer width="100%" height={420}>
              <LineChart
                data={serviceData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
      
                {granularity === "day" && highlightStart && highlightEnd && (
                  <ReferenceArea x1={highlightStart} x2={highlightEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
                {granularity === "day" && lastWeekStart && lastWeekEnd && (
                  <ReferenceArea x1={lastWeekStart} x2={lastWeekEnd} fill="rgba(173, 216, 230, 0.4)" />
                )}
      
                {mondayDates.map((date) => (
                  <ReferenceLine key={date} x={date} stroke="red" strokeDasharray="2 2" />
                ))}
                <ReferenceLine x={todayX} stroke="blue" strokeWidth={2} strokeDasharray="2 3" />
      
                <XAxis
                  dataKey="date"
                  tickFormatter={xTick}
                />
      
                <YAxis
                  domain={[0, yTop]}
                  tickFormatter={(value) => Math.round(value / 60)}
                  allowDecimals={false}
                  label={{ value: tr("Minutes"), angle: -90, position: "insideLeft" }}
                />
      
                <Tooltip
                  content={makeSortedPositiveTooltip({
                    labelFormatter: tooltipLabel,
                    valueFormatter: (v) => `${formatMinutesSmart(v)} ${tr("Minutes")}`,
                    isHidden: (k) => !!hiddenServiceLines?.[k],
                  })}
                />
      
                {/* Render ONLY visible lines so Y-axis rescales */}
                {keys.map((state, i) =>
                  hiddenServiceLines?.[state] ? null : (
                    <Line
                      key={state}
                      type="monotone"
                      dataKey={state}
                      stroke={colorForKey(state)}
                      name={state}
                      strokeWidth={i === keys.length - 1 ? 2 : 1}
                      dot={false}
                    />
                  )
                )}
              </LineChart>
            </ResponsiveContainer>
      
            <LegendBlock
              keys={keys}
              hiddenMap={hiddenServiceLines}
              onToggle={handleServiceLegendClick}
            />
          </div>
        );
      })()}
      <br/><br/>
      <h2>{tr('Layout\'s Heatmap')}</h2>
      { backgroundImage  && heatmapData ? (
        <div className="heatmap-wrapper">
          {/* Restaurant Layout Background Image */}
          <img
            ref={imageRef}
            src={backgroundImage}
            alt={tr('Restaurant Layout')}
            className="heatmap-bg"
            onLoad={() => {
              setImageSize({
                width: imageRef.current.clientWidth,
                height: imageRef.current.clientHeight
              });
            }}
          />
  
          {/* Heatmap Overlaid on Image */}
          <div className="heatmap-overlay">
            <ResponsiveContainer width="100%" height="100%" style={{ background: 'rgba(0, 50, 200, .35)' }}>
              <ScatterChart>
                <XAxis type="number" dataKey="x" hide domain={[0, imageSize.width]} />
            
                {/* IMPORTANT: image coordinates start from top-left */}
                <YAxis
                  type="number"
                  dataKey="y"
                  hide
                  domain={[0, imageSize.height]}
                />
            
                <ZAxis
                  type="number"
                  dataKey="reservation_count"
                  range={[100, 4000]}
                  name="Reservation Count"
                />
            
                <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            
                <Scatter
                  name="Heat"
                  data={scaledHeatmapData}
                  fill="red"
                  fillOpacity={0.03}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>        
      ) : (
        <div>{tr('Loading layout heat-map...')}</div>
      )}
      <OrdersReportsPage embedded />
    </div>
  );
};

export default Reports;
